import { MouseEvent, useEffect, useMemo } from 'react';

import { ApiNotification } from 'api/notification/types/ApiNotification';
import { useUserEnvironment } from 'modules/main/store/hooks/useUserEnvironment';
import { MappedNotification } from 'types/mappedNotifications/MappedNotification';
import { promoteAts } from 'utils/ats';
import {
  NOTIFICATIONS,
  NotificationStatus,
  useLocalNotifications,
  useUpdateLocalNotification,
} from 'utils/localNotifications';
import { getMappedNotificationIds } from 'utils/userNotifications';

import { LocalNotificationListItem } from './Item/variants/LocalNotificationListItem';
import { UserNotificationListItem } from './Item/variants/UserNotificationListItem';

type Props = {
  notifications: (ApiNotification | MappedNotification)[];
  dismissible?: boolean;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
};

export function NotificationList({
  notifications,
  dismissible,
  onClick,
}: Props) {
  const userEnvironment = useUserEnvironment();

  const { localNotifications, isReady, isSet } = useLocalNotifications();

  const updateLocalNotification = useUpdateLocalNotification();

  const sortedNotifications = useMemo(
    () =>
      notifications
        .slice()
        .sort((a, b) => Date.parse(b.created) - Date.parse(a.created)),
    [notifications],
  );

  // Only these notifications are shown in the dropdown:
  // - ATS CTA
  const localNotificationsFiltered = useMemo(
    () =>
      localNotifications.filter(
        (notification) =>
          notification.status === NotificationStatus.NEW &&
          notification.type === NOTIFICATIONS.ORG_ADMIN_USER_DROPDOWN_ATS_CTA &&
          promoteAts({ userEnvironment }),
      ),
    [localNotifications, userEnvironment],
  );

  // Set initial status for the notifications
  // These local notifications support the read/unread behavior
  useEffect(() => {
    if (isReady && !isSet(NOTIFICATIONS.ORG_ADMIN_USER_DROPDOWN_ATS_CTA)) {
      updateLocalNotification(NOTIFICATIONS.ORG_ADMIN_USER_DROPDOWN_ATS_CTA, {
        status: NotificationStatus.NEW,
        read: false,
      });
    }
  }, [isReady, localNotifications, isSet, updateLocalNotification]);

  if (!isReady) return null;

  return (
    <>
      {localNotificationsFiltered.map((notification) => (
        <LocalNotificationListItem
          key={notification.type}
          notification={notification}
          dismissible={Boolean(dismissible)}
          onClick={onClick}
        />
      ))}

      {sortedNotifications.map((notification) => (
        <UserNotificationListItem
          key={getMappedNotificationIds(notification).join()}
          notification={notification}
          dismissible={Boolean(dismissible)}
          onClick={onClick}
        />
      ))}

      {notifications.length === 0 &&
        localNotificationsFiltered.length === 0 && (
          <div style={{ padding: 20, width: '100%', textAlign: 'center' }}>
            {getText('You are all up to date! 🎉')}
          </div>
        )}
    </>
  );
}
